import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, IconButton, Paper, Typography } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SystemUpdateAltRoundedIcon from "@mui/icons-material/SystemUpdateAltRounded";
import useLatestAppRelease from "../@shared/hooks/useLatestAppRelease";
import {
  APP_VERSION,
  UPDATE_TYPE_LABELS,
  isVersionNewer,
} from "../@shared/utils/appVersion";

export default function AppUpdatePrompt() {
  const { latestRelease } = useLatestAppRelease();
  const [dismissedVersion, setDismissedVersion] = useState(null);

  useEffect(() => {
    if (!latestRelease?.version) {
      setDismissedVersion(null);
    }
  }, [latestRelease?.version]);

  const shouldShow = useMemo(() => {
    const latestVersion = latestRelease?.version;
    if (!latestVersion || dismissedVersion === latestVersion) return false;
    return isVersionNewer(latestVersion, APP_VERSION);
  }, [dismissedVersion, latestRelease?.version]);

  if (!shouldShow) return null;

  const typeLabel =
    UPDATE_TYPE_LABELS[latestRelease?.type] || latestRelease?.type || "Update";

  return (
    <Paper
      elevation={10}
      sx={{
        position: "fixed",
        top: { xs: 10, sm: 16 },
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 2200,
        width: { xs: "calc(100vw - 24px)", sm: 460 },
        maxWidth: "calc(100vw - 24px)",
        px: 1.25,
        py: 1,
        borderRadius: 1,
        display: "flex",
        alignItems: "center",
        gap: 1,
        border: "1px solid rgba(255,255,255,0.18)",
        background:
          "linear-gradient(135deg, rgba(12,8,28,0.98), rgba(38,21,69,0.96))",
        color: "#fff",
        boxShadow: "0 10px 26px rgba(0,0,0,0.38)",
      }}
    >
      <SystemUpdateAltRoundedIcon sx={{ color: "#ffd24d", flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 900, fontSize: 14 }}>
          Version {latestRelease.version} is available
        </Typography>
        <Typography
          sx={{
            color: "#d8cbff",
            fontSize: 12,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {typeLabel}. Refresh when you are ready.
        </Typography>
      </Box>
      <Button
        size="small"
        variant="contained"
        onClick={() => window.location.reload()}
        sx={{
          flexShrink: 0,
          bgcolor: "#ffd24d",
          color: "#21104f",
          fontWeight: 900,
          "&:hover": { bgcolor: "#ffe27a" },
        }}
      >
        Refresh
      </Button>
      <IconButton
        size="small"
        aria-label="Dismiss update prompt"
        onClick={() => setDismissedVersion(latestRelease.version)}
        sx={{ color: "#fff", flexShrink: 0 }}
      >
        <CloseRoundedIcon fontSize="small" />
      </IconButton>
    </Paper>
  );
}
